import { MotionValue, useScroll, useTransform } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

interface UseOptimizedScrollOptions {
  target?: React.RefObject<HTMLElement>
  container?: React.RefObject<HTMLElement>
  layoutEffect?: boolean
  optimizeScroll?: boolean
}

interface UseOptimizedScrollReturn {
  scrollY: MotionValue<number>
  scrollX: MotionValue<number>
  scrollYProgress: MotionValue<number>
  scrollXProgress: MotionValue<number>
  isReady: boolean
}

/**
 * Hook optimisé pour Framer Motion qui évite les avertissements
 * et améliore les performances de scroll
 */
export const useOptimizedScroll = (options: UseOptimizedScrollOptions = {}): UseOptimizedScrollReturn => {
  const { target, container, layoutEffect = false, optimizeScroll = true } = options

  const [isReady, setIsReady] = useState(false)
  const fallbackRef = useRef<HTMLDivElement>(null)

  // Utiliser le ref fourni ou créer un fallback
  const targetRef = target || fallbackRef

  // Configuration optimisée pour éviter les avertissements
  const scrollOptions = {
    target: targetRef,
    container,
    layoutEffect,
    offset: optimizeScroll ? ["start end", "end start"] as ["start end", "end start"] : undefined
  }

  // Hook Framer Motion avec configuration optimisée
  const { scrollY, scrollX, scrollYProgress, scrollXProgress } = useScroll(scrollOptions)

  // Attendre que le DOM soit prêt
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // Nettoyer les listeners si nécessaire
  useEffect(() => {
    if (!isReady || !optimizeScroll) return

    const handleResize = () => {
      // Recalculer les positions après resize
      window.requestAnimationFrame(() => {
        // Force refresh des valeurs de scroll
        scrollYProgress.set(scrollYProgress.get())
        scrollXProgress.set(scrollXProgress.get())
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isReady, optimizeScroll, scrollYProgress, scrollXProgress])

  return {
    scrollY,
    scrollX,
    scrollYProgress,
    scrollXProgress,
    isReady
  }
}

/**
 * Hook pour créer des transformations de scroll optimisées
 */
export const useOptimizedTransform = (
  scrollProgress: MotionValue<number>,
  inputRange: number[],
  outputRange: (number | string)[],
  options: { optimizeScroll?: boolean } = {}
) => {
  const { optimizeScroll = true } = options

  const transform = useTransform(
    scrollProgress,
    inputRange,
    outputRange as any,
    optimizeScroll ? {
      clamp: false
    } : undefined
  )

  return transform
}

/**
 * Hook pour optimiser les performances des animations
 */
export const useOptimizedAnimation = (enabled: boolean = true) => {
  const [shouldAnimate, setShouldAnimate] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setShouldAnimate(false)
      return
    }

    // Détecter les préférences de performance
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const nav = navigator as Navigator & { deviceMemory?: number }
    const isLowEndDevice = navigator.hardwareConcurrency < 4 ||
                          (nav.deviceMemory !== undefined && nav.deviceMemory < 4)

    setShouldAnimate(!prefersReducedMotion && !isLowEndDevice)
  }, [enabled])

  return shouldAnimate
}
